// AquaFlow water temperature + water age along the flow (buried pipe adapts to the soil temperature)
const num = (v) => (v === '' || v == null || !Number.isFinite(+v) ? null : +v);
const RHO_CP = 4.18e6;

// soil temperature at laying depth: damped towards the annual mean below 54 cm
export const soilAt = (clim, depth) => {
  if (!clim) return null;
  if (clim.manual != null) return clim.manual;
  const { t54, mean } = clim;
  if (t54 == null) return null;
  return mean == null ? t54 : mean + (t54 - mean) * Math.exp(-Math.max(0, depth - 0.54) / 2.5);
};

// current soil + air temperature (Open-Meteo, free) and annual mean air temperature of the last year
export async function loadClimate(lat, lon) {
  const ll = `latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}`;
  const f = await (await fetch(`https://api.open-meteo.com/v1/forecast?${ll}&current=temperature_2m&hourly=soil_temperature_54cm&forecast_days=1&timezone=auto`)).json();
  const end = new Date(Date.now() - 7 * 864e5), start = new Date(end - 365 * 864e5), d = (x) => x.toISOString().slice(0, 10);
  let mean = null, max54 = null, min54 = null;
  try {
    const a = await (await fetch(`https://archive-api.open-meteo.com/v1/archive?${ll}&start_date=${d(start)}&end_date=${d(end)}&daily=temperature_2m_mean,soil_temperature_28_to_100cm_mean&timezone=auto`)).json();
    const v = (a.daily?.temperature_2m_mean || []).filter(Number.isFinite);
    if (v.length) mean = v.reduce((s, x) => s + x, 0) / v.length;
    const s = (a.daily?.soil_temperature_28_to_100cm_mean || []).filter(Number.isFinite);
    if (s.length) { max54 = Math.max(...s); min54 = Math.min(...s); }
  } catch { /* archive optional */ }
  const h = (f.hourly?.soil_temperature_54cm || []).filter(Number.isFinite);
  return { air: f.current?.temperature_2m ?? null, t54: h.length ? h[Math.min(h.length - 1, new Date().getHours())] : null, mean, max54, min54, at: Date.now() };
}

// propagate along the flow: nodes in falling head order, mixing of inflows; U = heat transfer W/m²K
export function thermal(res, cfg, clim) {
  const tIn = num(cfg.tIn) ?? 10, U = num(cfg.U) ?? 2.5;
  const nodes = res.nodes, T = {}, age = {}, out = {};
  const inc = {};
  res.links.filter((l) => !l.dead).forEach((l) => {
    const fwd = l.q >= 0, from = fwd ? l.a : l.b, to = fwd ? l.b : l.a;
    (inc[to] ||= []).push({ l, from, q: Math.abs(l.q) });
  });
  const order = Object.values(nodes).sort((a, b) => b.head - a.head);
  order.forEach((n) => {
    const list = (inc[n.id] || []).filter((x) => x.q > 1e-6 && T[x.from] != null);
    if (!list.length) { const ts = soilAt(clim, 1.5); T[n.id] = res.srcIds?.includes(n.id) || n.src ? tIn : ts ?? tIn; age[n.id] = res.srcIds?.includes(n.id) || n.src ? 0 : Infinity; return; }
    let qs = 0, ts = 0, as = 0;
    list.forEach(({ l, from, q }) => {
      const tsoil = soilAt(clim, l.depth) ?? T[from], v = Math.abs(l.v), t = v > 1e-5 ? l.len / v : Infinity, tau = (RHO_CP * (l.d / 1000)) / (4 * U);
      const tOut = tsoil + (T[from] - tsoil) * Math.exp(-t / tau);
      out[l.id] = { tIn: T[from], tOut, hours: t / 3600 };
      qs += q; ts += q * tOut; as += q * (age[from] + t);
    });
    T[n.id] = ts / qs; age[n.id] = as / qs;
  });
  res.links.forEach((l) => { if (!out[l.id]) { const ts = soilAt(clim, l.depth); out[l.id] = { tIn: ts, tOut: ts, hours: Infinity, stag: true }; } });
  const ends = Object.values(nodes).filter((n) => !n.twin && n.demand > 0);
  const vals = Object.entries(T).filter(([id]) => !nodes[id]?.twin).map(([, v]) => v);
  return { T, age, links: out, max: Math.max(...vals), min: Math.min(...vals), endT: ends.map((n) => ({ id: n.id, fid: n.fid, t: T[n.id], h: age[n.id] / 3600 })) };
}

export const tWarn = (c) => (c == null ? null : c >= 25 ? 'hot' : c >= 20 ? 'warm' : c <= 0.5 ? 'frost' : null);

// without climate data: seasonal soil temperature at pipe depth for Central Europe (mean 10 °C, ±6 K, warmest mid-September)
export function seasonClim(scen, date = new Date()) {
  const doy = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 864e5);
  const t = scen === 'summer' ? 16 : scen === 'winter' ? 4 : 10 + 6 * Math.cos((2 * Math.PI * (doy - 258)) / 365);
  return { t54: +t.toFixed(1), mean: null, est: true };
}
